import { z } from "zod";
import { IdType, sortQuery } from "./base.validator";
import { fileMeta } from "./files.validator";
import { createdRole } from "./role.validator";
import { paginateSchema, paginationMeta } from "./pagination.validator";
import { createdUserStatus } from "./user-status.validator";

export const createdUser = z
	.object({
		id: IdType.describe("The unique identifier of the user"),
		email: z.string().email().describe("The email address of the user"),
		firstName: z.string().describe("The first name of the user"),
		lastName: z.string().describe("The last name of the user"),
		accountId: IdType.describe("The reference id to the account").nullish(),
		departmentId: IdType.describe(
			"The reference id to the department"
		).nullish(),
		roleId: IdType.describe("The reference id to the role"),
		profilePictureId: IdType.describe(
			"The reference id to the profilePicture"
		).nullish(),
		profile: z.object({
			jobTitle: z.string().nullable(),
			employmentStatus: z.string(),
		}),
	})
	.describe("The representation of the user that exists in the database");

export const createdUserRelationship = createdUser.extend({
	role: createdRole.optional(),
	profilePicture: fileMeta.optional(),
	userStatus: createdUserStatus.nullish(),
	profile: z
		.object({
			jobTitle: z.string().nullable(),
		})
		.optional(),
});

export type CreatedUserRelationshipDto = z.infer<
	typeof createdUserRelationship
>;

export const createdUserDetails = createdUser
	.omit({
		roleId: true,
		profilePictureId: true,
	})
	.extend({
		profilePicture: fileMeta.omit({ uploaderId: true }).nullable(),
		role: createdRole,
		profile: z.object({
			employmentStatus: z.string().nullable(),
			jobTitle: z.string().nullable(),
			departmentId: IdType.nullable(),
			managedDepartmentId: IdType.nullable(),
		}),
		userStatus: createdUserStatus.nullable(),
		hasPassword: z.boolean(),
		isLeaveApprover: z.boolean(),
	});
export type CreatedUserDetailsDto = z.infer<typeof createdUserDetails>;

export const paginatedCreatedUsers = paginateSchema(createdUserRelationship);
export type PaginatedCreatedUsersDto = z.infer<typeof paginatedCreatedUsers>;

export const findUserFilter = createdUser
	.pick({
		id: true,
		email: true,
		firstName: true,
		lastName: true,
	})
	.partial();
export const findUserInclude = z
	.object({
		profilePicture: z.coerce.boolean(),
		role: z.coerce.boolean(),
		userStatus: z.coerce.boolean(),
		profile: z.coerce.boolean(),
	})
	.partial();
export const findUserSort = createdUser
	.pick({ lastName: true, firstName: true })
	.extend({
		lastName: sortQuery,
		firstName: sortQuery,
	})
	.partial();

export const findUserQuery = z
	.object({
		sort: findUserSort,
		include: findUserInclude,
	})
	.merge(findUserFilter)
	.merge(paginationMeta)
	.partial();
